/**
 * useWizardSectionNav
 *
 * Bridges a wizard's positional `currentStep` (1-indexed into the descriptor
 * list) to `DetailPanelLayout`'s string-keyed section API: derives the active
 * section id, maps section clicks back to a step number, and builds the
 * `CollapsibleTabs` items (attention dot driven by `sectionValidity`). Shared by
 * every multi-section form wizard so the id↔index glue lives in one place.
 */
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  CollapsibleTabItem,
  TabIconComponent,
} from '@/components/common/CollapsibleTabs';

/** Minimal descriptor shape the section nav needs from a wizard step. */
interface SectionNavDescriptor {
  id: string;
  labelKey: string;
  icon: TabIconComponent;
}

interface UseWizardSectionNavParams<TDescriptor extends SectionNavDescriptor> {
  stepDescriptors: TDescriptor[];
  /** 1-indexed pointer into `stepDescriptors` (the form's `currentStep`). */
  currentStep: number;
  /** Per-section validity, same order as `stepDescriptors`; flips the dot. */
  sectionValidity: boolean[];
  /** Jump to a 1-indexed step position. */
  onStepSelect: (step: number) => void;
}

interface UseWizardSectionNavResult {
  activeSection: string;
  handleSectionChange: (id: string) => void;
  sectionItems: CollapsibleTabItem[];
}

export function useWizardSectionNav<TDescriptor extends SectionNavDescriptor>({
  stepDescriptors,
  currentStep,
  sectionValidity,
  onStepSelect,
}: UseWizardSectionNavParams<TDescriptor>): UseWizardSectionNavResult {
  const { t } = useTranslation();

  const activeSection =
    stepDescriptors[currentStep - 1]?.id ?? stepDescriptors[0]?.id ?? '';

  const handleSectionChange = useCallback(
    (id: string) => {
      const index = stepDescriptors.findIndex(
        (descriptor) => descriptor.id === id
      );
      if (index >= 0) onStepSelect(index + 1);
    },
    [stepDescriptors, onStepSelect]
  );

  const sectionItems = useMemo<CollapsibleTabItem[]>(
    () =>
      stepDescriptors.map((descriptor, index) => ({
        id: descriptor.id,
        label: t(descriptor.labelKey),
        icon: descriptor.icon,
        indicator: !sectionValidity[index],
      })),
    [stepDescriptors, t, sectionValidity]
  );

  return { activeSection, handleSectionChange, sectionItems };
}
